import { Header, Sidebar } from '@/components/layout';
import { SectionError, SectionLoading } from '@/components/section-state';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Play } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ModelPicker } from './ModelPicker';
import { PromptEditor } from './PromptEditor';
import { ResultCards } from './ResultCards';
import { RunDetails } from './RunDetails';
import { Scorecard } from './Scorecard';
import { SessionPreview } from './SessionPreview';
import { buildEvaluateBody, buildGenerateBody } from './api';
import type { ReviewMode } from './types';
import {
  useEvaluateSummaries,
  useGenerateComparison,
  useModelCatalog,
  usePrompts,
  useSessions,
  useTemplates,
  useTranscriptPreview,
} from './useModelReviewQueries';

export const ModelReviewPage = () => {
  const { toast } = useToast();

  const catalog = useModelCatalog();
  const prompts = usePrompts();
  const templates = useTemplates();
  const sessions = useSessions();
  const generate = useGenerateComparison();
  const evaluate = useEvaluateSummaries();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [mode, setMode] = useState<ReviewMode>('conversation');
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [promptId, setPromptId] = useState<string>('');
  const [editedPrompt, setEditedPrompt] = useState('');
  // The prompt box is auto-seeded with a CONVERSATION base prompt. Sending that as a
  // dictation override would replace the production dictation rules and template
  // with conversation-summarisation instructions. Only an actual edit is an override.
  const [promptEdited, setPromptEdited] = useState(false);
  // null until the catalog loads, then seeded from the server's own defaults so the
  // two sides cannot drift. Sending a stale hardcoded value would defeat the
  // backend default entirely.
  const [maxTokens, setMaxTokens] = useState<number | null>(null);
  const [judgeModel, setJudgeModel] = useState<string | null>(null);

  const transcript = useTranscriptPreview(sessionId);
  const models = catalog.data?.models ?? [];
  const defaults = catalog.data?.defaults;

  // The backend rejects a judge that is among the summaries. Exclude the contestants
  // — but once results exist, exclude the models in THAT RUN, not the live checkbox
  // state. Otherwise deselecting a model after generating makes it a legal judge
  // while its summary is still in the payload, and evaluation 400s.
  const contestedIds = generate.data ? generate.data.results.map((r) => r.model) : selectedModels;
  const judgeOptions = models.filter((m) => m.can_judge && !contestedIds.includes(m.id));

  useEffect(() => {
    if (!defaults) return;
    setMaxTokens((current) => current ?? defaults.max_tokens);
    setJudgeModel((current) => current ?? defaults.judge_model);
  }, [defaults]);

  // Selecting the current judge as a contestant must move the judge, not dead-end.
  useEffect(() => {
    if (judgeModel && contestedIds.includes(judgeModel)) {
      setJudgeModel(judgeOptions[0]?.id ?? null);
    }
  }, [judgeModel, contestedIds, judgeOptions]);

  // Preserved from the old page: it selected a prompt on load, preferring the
  // default, so the textarea is never empty when you first hit generate.
  useEffect(() => {
    const available = prompts.data?.prompts ?? [];
    if (promptId || available.length === 0) return;
    const initial = available.find((p) => p.is_default) ?? available[0];
    setPromptId(initial.id);
    setEditedPrompt(initial.content);
    setPromptEdited(false);
  }, [prompts.data, promptId]);

  // Production applies the session's own language; mirroring it keeps the
  // comparison prompt identical to what that session would really have received.
  const sessionLanguage = (sessions.data ?? []).find((s) => s.id === sessionId)?.language ?? 'is';

  // Results belong to the configuration that produced them. Without this reset,
  // changing the session leaves the old cards on screen AND handleEvaluate pairs the
  // previous transcript with the new sessionId — which is what the backend writes as
  // the evaluation's audit resource id.
  const { reset: resetGenerate } = generate;
  const { reset: resetEvaluate } = evaluate;
  // sessionId/mode/templateId are triggers, not values the body reads — changing any
  // of them is precisely what must invalidate the previous run.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are intentional triggers
  useEffect(() => {
    resetGenerate();
    resetEvaluate();
  }, [sessionId, mode, templateId, resetGenerate, resetEvaluate]);

  const transcriptText = useMemo(() => {
    const segments = transcript.data?.segments ?? [];
    return [...segments]
      .sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0))
      .map((segment) => {
        const text = (segment.text ?? '').trim();
        if (!text) return '';
        return segment.speaker_label ? `[${segment.speaker_label}]: ${text}` : text;
      })
      .filter(Boolean)
      .join('\n');
  }, [transcript.data]);

  const toggleModel = (modelId: string) =>
    setSelectedModels((prev) => (prev.includes(modelId) ? prev.filter((id) => id !== modelId) : [...prev, modelId]));

  const applyPrompt = (nextPromptId: string) => {
    setPromptId(nextPromptId);
    const chosen = (prompts.data?.prompts ?? []).find((p) => p.id === nextPromptId);
    if (chosen) {
      setEditedPrompt(chosen.content);
      setPromptEdited(false);
    }
  };

  const resetPrompt = () => {
    const chosen = (prompts.data?.prompts ?? []).find((p) => p.id === promptId);
    if (chosen) {
      setEditedPrompt(chosen.content);
      setPromptEdited(false);
    }
  };

  const handleGenerate = () => {
    if (!sessionId) {
      toast({ title: 'Villa', description: 'Veldu lotu fyrst', variant: 'destructive' });
      return;
    }
    if (selectedModels.length === 0) {
      toast({ title: 'Villa', description: 'Veldu að minnsta kosti eitt líkan', variant: 'destructive' });
      return;
    }
    if (!maxTokens) {
      toast({ title: 'Villa', description: 'Hámark tóka vantar', variant: 'destructive' });
      return;
    }

    // A new run invalidates the old scorecard; without this the previous
    // evaluation stays on screen beside fresh, unevaluated results.
    resetEvaluate();

    generate.mutate(
      buildGenerateBody({
        sessionId,
        models: selectedModels,
        mode,
        // In dictation the seeded conversation prompt must NOT override the
        // dictation rules; only a real edit counts.
        prompt: mode === 'dictation' && !promptEdited ? '' : editedPrompt,
        maxTokens,
        templateId,
        language: sessionLanguage,
      }),
      {
        onSuccess: (data) => toast({ title: 'Lokið', description: `${data.results.length} samantektir útbúnar` }),
        onError: (error) => toast({ title: 'Villa', description: error.message, variant: 'destructive' }),
      }
    );
  };

  const handleEvaluate = () => {
    const results = generate.data;
    if (!results || !sessionId || !judgeModel) return;

    // Preserved from the old page: evaluating nothing produced a confusing empty
    // scorecard, so refuse before spending a judge call.
    if (results.results.every((r) => !r.summary || r.error)) {
      toast({ title: 'Villa', description: 'Engar samantektir til að meta', variant: 'destructive' });
      return;
    }

    evaluate.mutate(
      buildEvaluateBody({
        sessionId,
        transcript: results.transcript,
        promptUsed: results.prompt_used,
        judgeModel,
        language: sessionLanguage,
        results: results.results,
        catalog: models,
      }),
      {
        onSuccess: (data) => {
          if (data.status === 'partial') {
            toast({
              title: 'Mat lokið',
              description: 'Niðurstöður bárust en gátu ekki verið greindar sem JSON',
              variant: 'destructive',
            });
          } else if (data.status === 'error') {
            toast({
              title: 'Villa',
              description: data.error ?? 'Mistókst að meta samantektir',
              variant: 'destructive',
            });
          }
        },
        onError: (error) => toast({ title: 'Villa', description: error.message, variant: 'destructive' }),
      }
    );
  };

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <Header title="Módelprófun" />
        <main className="space-y-6 p-6">
          <Card>
            <CardHeader>
              <CardTitle>Stillingar</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="session">Lota</Label>
                {sessions.isError && (
                  <SectionError title="Ekki tókst að sækja lotur" error={sessions.error} onRetry={sessions.refetch} />
                )}
                <Select value={sessionId ?? undefined} onValueChange={setSessionId}>
                  <SelectTrigger id="session">
                    <SelectValue placeholder="Veldu lotu" />
                  </SelectTrigger>
                  <SelectContent>
                    {(sessions.data ?? []).map((session) => (
                      <SelectItem key={session.id} value={session.id}>
                        {session.topic || session.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Líkön</Label>
                {catalog.isLoading && <SectionLoading label="Sæki líkön" />}
                {catalog.isError && (
                  <SectionError title="Ekki tókst að sækja líkön" error={catalog.error} onRetry={catalog.refetch} />
                )}
                {catalog.isSuccess && <ModelPicker models={models} selected={selectedModels} onToggle={toggleModel} />}
              </div>

              <div className="space-y-2">
                <Label htmlFor="mode">Tegund</Label>
                <Select value={mode} onValueChange={(value) => setMode(value as ReviewMode)}>
                  <SelectTrigger id="mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conversation">Samtal</SelectItem>
                    <SelectItem value="dictation">Upplestur</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="template">Sniðmát</Label>
                {templates.isError && (
                  <SectionError
                    title="Ekki tókst að sækja sniðmát"
                    error={templates.error}
                    onRetry={templates.refetch}
                  />
                )}
                <Select
                  value={templateId ? String(templateId) : undefined}
                  onValueChange={(value) => setTemplateId(value === 'none' ? null : Number(value))}
                >
                  <SelectTrigger id="template">
                    <SelectValue placeholder="Ekkert sniðmát" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* The current page offers an explicit clear; without it a
                        template cannot be deselected once chosen. */}
                    <SelectItem value="none">Ekkert sniðmát</SelectItem>
                    {(templates.data?.templates ?? []).map((template) => (
                      <SelectItem key={template.id} value={String(template.id)}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <PromptEditor
                prompts={prompts.data?.prompts ?? []}
                promptId={promptId}
                value={editedPrompt}
                isError={prompts.isError}
                error={prompts.error}
                onRetry={prompts.refetch}
                onSelect={applyPrompt}
                onChange={(next) => {
                  setEditedPrompt(next);
                  setPromptEdited(true);
                }}
                onReset={resetPrompt}
              />

              <div className="space-y-2">
                <Label htmlFor="max-tokens">Hámark tóka</Label>
                <Input
                  id="max-tokens"
                  type="number"
                  min={1}
                  max={32000}
                  value={maxTokens ?? ''}
                  onChange={(event) => {
                    // Clearing the field must not become 0 — that is a guaranteed 400.
                    const next = Number.parseInt(event.target.value, 10);
                    setMaxTokens(Number.isNaN(next) ? null : next);
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="judge">Matslíkan</Label>
                <Select value={judgeModel ?? undefined} onValueChange={setJudgeModel}>
                  <SelectTrigger id="judge" disabled={judgeOptions.length === 0}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {judgeOptions.map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {sessionId && (
                <div className="lg:col-span-2">
                  <SessionPreview
                    isLoading={transcript.isLoading}
                    transcriptText={transcriptText}
                    modelSummary={transcript.data?.model_summary?.summary_text}
                    userSummary={transcript.data?.user_summary?.summary_text}
                    isError={transcript.isError}
                    error={transcript.error}
                    onRetry={transcript.refetch}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Button onClick={handleGenerate} disabled={generate.isPending} className="gap-2">
            {generate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Búa til samantektir
          </Button>

          {generate.isSuccess && (
            <>
              <RunDetails promptUsed={generate.data.prompt_used} transcript={generate.data.transcript} />

              <ResultCards results={generate.data.results} catalog={models} />

              <Button onClick={handleEvaluate} disabled={evaluate.isPending || !judgeModel} variant="outline">
                Meta samantektir
              </Button>
              {!judgeModel && (
                <p className="text-muted-foreground text-xs">
                  Ekkert líkan eftir til að meta. Taktu eitt líkan úr samanburðinum til að nota það sem matslíkan.
                </p>
              )}
            </>
          )}

          {evaluate.isSuccess && evaluate.data.truncated && (
            <p className="text-muted-foreground text-xs">
              Athugið: umritun eða samantektir voru styttar áður en matslíkanið sá þær.
            </p>
          )}

          {evaluate.isSuccess && evaluate.data.evaluation && (
            <Scorecard
              evaluation={evaluate.data.evaluation}
              judgeModel={evaluate.data.judge_model ?? judgeModel ?? ''}
            />
          )}
        </main>
      </div>
    </div>
  );
};
